/**
 * Utilidad para obtener mensajes de error traducidos basados en códigos de error del backend
 */

/**
 * Obtiene el mensaje de error traducido basado en el código de error del backend
 * @param error - Objeto de error de la API
 * @param t - Función de traducción de i18next
 * @returns Mensaje de error traducido
 */
export const getExcludedProductErrorMessage = (
  error: any,
  t: (key: string, params?: Record<string, string | number>) => string
): string => {
  // Extraer código de error del response (puede estar en diferentes lugares)
  // El backend puede enviar error_code o code
  const responseData = error?.response?.data || {};
  const errorCode =
    responseData.error_code ||
    responseData.error?.code ||
    responseData.error?.error_code ||
    responseData.code ||
    error?.code ||
    error?.error_code;

  // Extraer datos adicionales del error (para reemplazar placeholders)
  const errorData = responseData.error || responseData || {};
  const errorMessage =
    responseData.error?.message ||
    responseData.message ||
    error?.message;

  // Si hay un código de error conocido, usar la traducción correspondiente
  if (errorCode) {
    const translationKey = `external_excluded.create.error.codes.${errorCode}`;
    
    // Extraer parámetros del error para reemplazar en el mensaje
    // El backend puede enviar los parámetros en diferentes formatos
    const params: Record<string, string | number> = {};
    
    // Intentar extraer id (puede venir como id, external_product_id, external_variant_id)
    if (errorData.id) params.id = errorData.id;
    else if (errorData.external_product_id) params.id = errorData.external_product_id;
    else if (errorData.external_variant_id) params.id = errorData.external_variant_id;
    else if (responseData.id) params.id = responseData.id;
    
    // Extraer shop_id y team_id directamente de errorData o responseData
    if (errorData.shop_id) params.shop_id = errorData.shop_id;
    else if (responseData.shop_id) params.shop_id = responseData.shop_id;
    
    if (errorData.team_id) params.team_id = errorData.team_id;
    else if (responseData.team_id) params.team_id = responseData.team_id;
    
    // Si no están en errorData, intentar extraerlos del mensaje del backend
    // Ejemplo: "Shop 321 does not belong to team 111."
    if (!params.shop_id || !params.team_id) {
      const message = errorMessage || '';
      // Buscar patrones como "Shop {number}" y "team {number}"
      const shopMatch = message.match(/Shop\s+(\d+)/i);
      const teamMatch = message.match(/team\s+(\d+)/i);
      
      if (shopMatch && !params.shop_id) {
        params.shop_id = Number(shopMatch[1]);
      }
      if (teamMatch && !params.team_id) {
        params.team_id = Number(teamMatch[1]);
      }
    }
    
    // Asegurar que los valores sean números o strings (i18next puede necesitar strings)
    if (params.shop_id !== undefined) params.shop_id = String(params.shop_id);
    if (params.team_id !== undefined) params.team_id = String(params.team_id);
    if (params.id !== undefined) params.id = String(params.id);
    if (params.value !== undefined) params.value = String(params.value);
    
    // Extraer value (para EXCLUDED_PRODUCTS_AMOUNT_HANDLING_INVALID)
    if (errorData.value) params.value = errorData.value;
    else if (errorData.excluded_products_amount_handling) {
      params.value = errorData.excluded_products_amount_handling;
    }

    console.log('🔍 getExcludedProductErrorMessage - Debug:', {
      errorCode,
      translationKey,
      params,
      errorData,
      responseData,
      errorMessage,
    });

    const translatedMessage = t(translationKey, params);

    console.log('🔍 getExcludedProductErrorMessage - Translated:', translatedMessage);

    // Si la traducción existe (no devuelve la key), usarla
    if (translatedMessage !== translationKey) {
      return translatedMessage;
    }
  }

  // Si hay un mensaje directo del backend pero no hay código de error conocido,
  // intentar usar el mensaje (aunque esté en inglés, es mejor que nada)
  if (errorMessage && typeof errorMessage === 'string') {
    return errorMessage;
  }

  // Fallback al mensaje genérico
  return t('external_excluded.create.error.unexpected');
};
